import type { PatchOp } from "@/lib/blocks";
import type {
  ApiToken,
  CreatedToken,
  Identity,
  Note,
  NoteSummary,
  NoteTreeNode,
  NoteVersion,
  OAuthDecision,
  OAuthRequest,
  RecentPayload,
  SearchHit,
  Share,
  SharedNote,
  SignInProviders,
  Space,
  Thread,
  User,
} from "./types";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? ApiError.messageFrom(status, body));
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  static messageFrom(status: number, body: unknown): string {
    if (body && typeof body === "object") {
      const b = body as Record<string, unknown>;
      const m = b.message ?? b.error;
      if (Array.isArray(m)) return m.map(String).join("; ");
      if (typeof m === "string" && m) return m;
      if (m && typeof m === "object" && typeof (m as { message?: unknown }).message === "string")
        return (m as { message: string }).message;
    }
    if (status === 401) return "Not signed in";
    if (status === 403) return "Not allowed";
    if (status === 404) return "Not found";
    if (status === 409) return "The note changed elsewhere";
    return `Request failed (${status})`;
  }
}

export function isApiError(e: unknown, status?: number): e is ApiError {
  return e instanceof ApiError && (status === undefined || e.status === status);
}

export function errorMessage(e: unknown, fallback = "Something went wrong"): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

const BASE = "/api";

type Query = Record<string, string | number | boolean | null | undefined>;

function qs(q?: Query): string {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export async function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  query?: Query,
): Promise<T> {
  const mock = await mockRequest<T>(method, path, body, query);
  if (mock !== NO_MOCK) return mock as T;

  const res = await fetch(`${BASE}${path}${qs(query)}`, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

/** Tolerate list endpoints that wrap items in an object. */
function asList<T>(r: unknown): T[] {
  if (Array.isArray(r)) return r as T[];
  if (r && typeof r === "object") {
    const o = r as Record<string, unknown>;
    for (const k of ["items", "notes", "threads", "hits", "tokens", "shares", "spaces", "data"]) {
      if (Array.isArray(o[k])) return o[k] as T[];
    }
  }
  return [];
}

/* ---------------- auth ---------------- */

export const api = {
  auth: {
    me: () => request<User>("GET", "/auth/me"),
    logout: () => request<void>("POST", "/auth/logout"),
    providers: () => request<SignInProviders>("GET", "/auth/providers"),
    identities: () => request<unknown>("GET", "/auth/identities").then((r) => asList<Identity>(r)),
    unlink: (id: string) => request<void>("DELETE", `/auth/identities/${id}`),
  },

  spaces: {
    list: () => request<unknown>("GET", "/spaces").then((r) => asList<Space>(r)),
    get: (id: string) => request<Space>("GET", `/spaces/${id}`),
    create: (name: string) => request<Space>("POST", "/spaces", { name }),
    notes: (id: string, q?: { q?: string; limit?: number; cursor?: string }) =>
      request<unknown>("GET", `/spaces/${id}/notes`, undefined, q).then((r) =>
        asList<NoteSummary>(r),
      ),
    tree: (id: string) =>
      request<unknown>("GET", `/spaces/${id}/tree`).then((r) => asList<NoteTreeNode>(r)),
    createNote: (
      id: string,
      body: { title?: string; markdown: string; parentId?: string | null },
    ) => request<Note>("POST", `/spaces/${id}/notes`, body),
  },

  notes: {
    get: (id: string) => request<Note>("GET", `/notes/${id}`),
    patch: (id: string, version: number, ops: PatchOp[]) =>
      request<Note>("PATCH", `/notes/${id}`, { version, ops }),
    put: (id: string, body: { version: number; markdown: string; title?: string }) =>
      request<Note>("PUT", `/notes/${id}`, body),
    remove: (id: string) => request<void>("DELETE", `/notes/${id}`),
    move: (id: string, body: { parentId: string | null; position?: number }) =>
      request<Note>("POST", `/notes/${id}/move`, body),
    versions: (id: string) =>
      request<unknown>("GET", `/notes/${id}/versions`).then((r) => asList<NoteVersion>(r)),
    shares: (id: string) =>
      request<unknown>("GET", `/notes/${id}/shares`).then((r) => asList<Share>(r)),
    createShare: (id: string, body: { oneTime?: boolean; expiresInHours?: number }) =>
      request<Share>("POST", `/notes/${id}/shares`, body),
  },

  shares: {
    revoke: (id: string) => request<void>("DELETE", `/shares/${id}`),
    // GET /share/:token answers { note: {...}, oneTime, expiresAt }; flatten it for the page.
    read: (token: string) =>
      request<Partial<SharedNote> & { note?: Partial<SharedNote> }>("GET", `/share/${encodeURIComponent(token)}`).then(
        (r): SharedNote => ({
          title: r.note?.title ?? r.title ?? null,
          blocks: r.note?.blocks ?? r.blocks ?? [],
          outline: r.note?.outline ?? r.outline,
          updatedAt: r.note?.updatedAt ?? r.updatedAt,
          oneTime: r.oneTime,
          expiresAt: r.expiresAt ?? null,
        }),
      ),
  },

  search: (q: string, opts?: { space?: string; limit?: number }) =>
    request<unknown>("GET", "/search", undefined, { q, ...opts }).then((r) =>
      asList<SearchHit>(r),
    ),

  threads: {
    list: (q?: { space?: string; limit?: number; cursor?: string }) =>
      request<unknown>("GET", "/threads", undefined, q).then((r) => asList<Thread>(r)),
    get: (id: string) => request<Thread>("GET", `/threads/${id}`),
    moveNote: (id: string, noteId: string, toThreadId: string | null) =>
      request<{ sourceDeleted: boolean }>("POST", `/threads/${id}/notes/${noteId}/move`, { toThreadId }),
  },

  recent: (space?: string) =>
    request<Partial<RecentPayload>>("GET", "/recent", undefined, { space }).then((r) => ({
      threads: asList<Thread>(r?.threads ?? []),
      notes: asList<NoteSummary>(r?.notes ?? []),
    })),

  tokens: {
    list: () => request<unknown>("GET", "/tokens").then((r) => asList<ApiToken>(r)),
    create: (name: string) => request<CreatedToken>("POST", "/tokens", { name }),
    revoke: (id: string) => request<void>("DELETE", `/tokens/${id}`),
  },

  oauth: {
    request: (id: string) => request<OAuthRequest>("GET", `/oauth/requests/${id}`),
    decide: (id: string, allow: boolean) =>
      request<OAuthDecision>("POST", `/oauth/requests/${id}/decision`, { allow }),
  },
};

/* ---------------- optional mock mode (VITE_MOCK=1) ---------------- */

const NO_MOCK = Symbol("no-mock");

async function mockRequest<T>(
  method: string,
  path: string,
  body: unknown,
  query?: Query,
): Promise<T | typeof NO_MOCK> {
  if (import.meta.env.VITE_MOCK !== "1") return NO_MOCK;
  const mod = await import("./mock");
  return mod.handle(method, path, body, query) as T | typeof NO_MOCK;
}

export { NO_MOCK };
